import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Card, Row, Col, Form, Spinner, Alert, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import CsLineIcons from "../../../cs-line-icons";
import "react-datepicker/dist/react-datepicker.css";
import "./agenda.css";

import EventModal from "../../../layout/calendar/modals/event";
import SegmentedControl from "../../../layout/segmented-control";

import {
    mine as myCalendars
} from "../../../actions/calendar";

import {
    save as saveEvent,
    destroy as deleteEvent,
    send as sendEvent,
    mapped as mappedEvent,
    list as listEvents
} from "../../../actions/calendar.event";

import { isGuid } from "../../../utils/string";
import { normalizeCalendarDescription } from "../../../utils/calendarDescription";
import {
    BOARD_TASK_MODES,
    BOARD_TASK_MODE_OPTIONS,
    boardTaskCalendarColorKey,
    boardTaskEventMatchesMode,
    calendarLabel,
    currentPersonUuid,
    isBoardTasksCalendar
} from "../../../utils/boardTasksCalendar";

class Agenda extends React.Component {
    constructor(props) {
        super(props);

        const selectedDate = new Date();
        const visible = this.normalizeAgendaRange(selectedDate);

        this.state = {
            selectedDate,
            visible,

            calendars: [],
            visibleCalendarUuids: null,
            boardTaskMode: BOARD_TASK_MODES.MY,

            events: [],
            loading: false,
            error: null,

            modal: {
                event: {
                    show: false
                }
            }
        };
    }

    componentDidMount = async () => {
        this.__isMounted = true;

        await this.loadData();
    };

    componentWillUnmount = () => {
        this.__isMounted = false;
    };

    newEvent = () => {
        const start = new Date(this.state.selectedDate || new Date());
        start.setHours(9, 0, 0, 0);

        const end = new Date(start);
        end.setMinutes(end.getMinutes() + 30);

        this.openEventModal({
            uuid: null,
            type: "Event",
            time: {
                start,
                end
            }
        });
    };

    normalizeAgendaRange = (date) => {
        const start = date instanceof Date
            ? new Date(date)
            : new Date();

        start.setHours(0, 0, 0, 0);

        const end = new Date(start);
        end.setDate(end.getDate() + 7);
        end.setHours(23, 59, 59, 999);

        return {
            start,
            end
        };
    };

    hashString = (str) => {
        let h = 0;
        const s = String(str || "");

        for (let i = 0; i < s.length; i++) {
            h = ((h << 5) - h) + s.charCodeAt(i);
            h |= 0;
        }

        return Math.abs(h);
    };

    calendarColor = (calendarUuid) => {
        const h = this.hashString(calendarUuid);
        const hue = h % 360;

        return `hsl(${hue}, 70%, 40%)`;
    };

    calendarDisplayColor = (calendar, fallbackUuid = null) => {
        return this.calendarColor(boardTaskCalendarColorKey(calendar, fallbackUuid));
    };

    calendarFilterGroups = () => {
        const groups = [];

        (this.state.calendars || [])
            .filter((calendar) => isGuid(calendar?.uuid))
            .forEach((calendar) => {
                if (isBoardTasksCalendar(calendar)) {
                    return;
                }

                groups.push({
                    key: calendar.uuid,
                    label: calendarLabel(calendar),
                    calendar,
                    uuids: [calendar.uuid]
                });
            });

        return groups;
    };

    boardTaskCalendarUuids = () => {
        return (this.state.calendars || [])
            .filter((calendar) => isGuid(calendar?.uuid) && isBoardTasksCalendar(calendar))
            .map((calendar) => calendar.uuid);
    };

    ensureCalendarSelection = (calendars) => {
        const all = (calendars || [])
            .map((calendar) => calendar.uuid)
            .filter(isGuid);

        this.setState((prev) => {
            const current = prev.visibleCalendarUuids;

            if (current === null || typeof current === "undefined") {
                return {
                    ...prev,
                    visibleCalendarUuids: all
                };
            }

            if (Array.isArray(current) && current.length === 0) {
                return prev;
            }

            const pruned = (Array.isArray(current) ? current : [])
                .filter((uuid) => all.includes(uuid));

            return {
                ...prev,
                visibleCalendarUuids: pruned
            };
        });
    };

    hasVisibleEvents = () => {
        return (this.state.events || []).some(this.isEventVisible);
    };

    toggleCalendarGroupVisible = (uuids) => {
        const targetUuids = (uuids || []).filter(isGuid);

        if (targetUuids.length === 0) {
            return;
        }

        this.setState((prev) => {
            const all = (prev.calendars || [])
                .map((calendar) => calendar.uuid)
                .filter(isGuid);

            const current =
                prev.visibleCalendarUuids === null ||
                    typeof prev.visibleCalendarUuids === "undefined"
                    ? all
                    : Array.isArray(prev.visibleCalendarUuids)
                        ? prev.visibleCalendarUuids
                        : [];

            const targetSet = new Set(targetUuids);
            const allTargetsVisible = targetUuids.every((uuid) => current.includes(uuid));

            const next = allTargetsVisible
                ? current.filter((item) => !targetSet.has(item))
                : [...new Set([...current, ...targetUuids])];

            return {
                ...prev,
                visibleCalendarUuids: next
            };
        });
    };

    setBoardTaskMode = (mode) => {
        const nextMode = Object.values(BOARD_TASK_MODES).includes(mode)
            ? mode
            : BOARD_TASK_MODES.MY;

        this.setState((prev) => {
            const all = (prev.calendars || [])
                .map((calendar) => calendar.uuid)
                .filter(isGuid);
            const boardTaskUuids = (prev.calendars || [])
                .filter((calendar) => isGuid(calendar?.uuid) && isBoardTasksCalendar(calendar))
                .map((calendar) => calendar.uuid);
            const boardTaskSet = new Set(boardTaskUuids);
            const current =
                prev.visibleCalendarUuids === null ||
                    typeof prev.visibleCalendarUuids === "undefined"
                    ? all
                    : Array.isArray(prev.visibleCalendarUuids)
                        ? prev.visibleCalendarUuids
                        : [];

            const withoutBoardTasks = current.filter((uuid) => !boardTaskSet.has(uuid));
            const visibleCalendarUuids = nextMode === BOARD_TASK_MODES.HIDE
                ? withoutBoardTasks
                : [...new Set([...withoutBoardTasks, ...boardTaskUuids])];

            return {
                ...prev,
                boardTaskMode: nextMode,
                visibleCalendarUuids
            };
        });
    };

    showAllCalendars = () => {
        const all = (this.state.calendars || [])
            .map((calendar) => calendar.uuid)
            .filter(isGuid);

        this.setState({
            visibleCalendarUuids: all,
            boardTaskMode: BOARD_TASK_MODES.ALL
        });
    };

    showNoCalendars = () => {
        this.setState({
            visibleCalendarUuids: [],
            boardTaskMode: BOARD_TASK_MODES.HIDE
        });
    };

    loadData = async (range = this.state.visible) => {
        const start = range.start instanceof Date
            ? range.start
            : new Date(range.start);

        const end = range.end instanceof Date
            ? range.end
            : new Date(range.end);

        const startAt = start.toISOString();
        const endAt = end.toISOString();

        this.setState({
            loading: true,
            error: null
        });

        try {
            const calResp = await this.props.myCalendars();

            if (!this.__isMounted) {
                return;
            }

            const calendars = calResp?.items ?? calResp?.calendars ?? calResp ?? [];

            const perCalendar = await Promise.all(
                calendars
                    .filter((calendar) => isGuid(calendar?.uuid))
                    .map(async (calendar) => {
                        const eventsResp = await this.props.listEvents({
                            startAt,
                            endAt,
                            calendar: calendar.uuid
                        });

                        const eventsRaw = eventsResp?.items ?? eventsResp?.events ?? eventsResp ?? [];

                        return eventsRaw.map((event) => {
                            return this.normalizeEvent(event, calendar);
                        });
                    })
            );

            if (!this.__isMounted) {
                return;
            }

            const events = perCalendar
                .flat()
                .filter((event) => event.start instanceof Date && !isNaN(event.start.getTime()))
                .sort((a, b) => a.start - b.start);

            this.setState(
                {
                    calendars,
                    events,
                    visible: {
                        start,
                        end
                    },
                    loading: false
                },
                () => {
                    this.ensureCalendarSelection(calendars);
                }
            );
        } catch (error) {
            console.error("dashboard agenda loadData failed:", error);

            if (!this.__isMounted) {
                return;
            }

            this.setState({
                loading: false,
                error: "Unable to load agenda."
            });
        }
    };

    normalizeEvent = (event, calendar) => {
        const evStart = event.startsAt ?? event.start ?? event.startAt ?? event.start_time;
        const evEnd = event.endsAt ?? event.end ?? event.endAt ?? event.end_time;

        const start = evStart instanceof Date ? evStart : new Date(evStart);
        let end = evEnd instanceof Date ? evEnd : new Date(evEnd);

        if (!end || isNaN(end.getTime()) || end <= start) {
            end = new Date(start.getTime() + 30 * 60 * 1000);
        }

        const person =
            event.db?.person ||
            event.Person ||
            event.person ||
            null;

        const personUuid =
            event.db?.personUuid ||
            person?.uuid ||
            event.personUuid ||
            null;

        return {
            ...event,

            id: event.db?.uuid ?? event.uuid ?? event.id,
            uuid: event.db?.uuid ?? event.uuid ?? event.id,
            sourceUuid: event.db?.sourceUuid ?? event.sourceUuid ?? event.uuid ?? event.id ?? null,

            calendarUuid:
                event.db?.calendarUuid ||
                event.calendar?.uuid ||
                event.calendarUuid ||
                calendar.uuid,

            calendar: event.db?.calendar || event.calendar || calendar,
            boardTask: event.db?.boardTask || event.boardTask || null,

            title: event.title ?? event.name ?? event.summary ?? "(untitled)",
            description: normalizeCalendarDescription(event.description),
            type: event.type ?? "Event",

            start,
            end,
            allDay: !!(event.allDay ?? event.all_day),

            person,
            personUuid,

            caseUuid: event.db?.caseUuid || event.caseUuid || null,
            db: event.db || null
        };
    };

    onDateChange = async (date) => {
        const selectedDate = date instanceof Date
            ? date
            : new Date();

        const visible = this.normalizeAgendaRange(selectedDate);

        this.setState({
            selectedDate,
            visible
        });

        await this.loadData(visible);
    };

    onTodayClick = async () => {
        const selectedDate = new Date();
        const visible = this.normalizeAgendaRange(selectedDate);

        this.setState({
            selectedDate,
            visible
        });

        await this.loadData(visible);
    }

    openEventModal = ({ uuid = null, sourceUuid = null, type = "Event", time = null, ...event } = {}) => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                event: {
                    ...prev.modal.event,
                    ...event,
                    show: true,
                    uuid,
                    sourceUuid,
                    type,
                    ...(time ? { time } : {})
                }
            }
        }));
    };

    closeEventModal = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                event: {
                    ...prev.modal.event,
                    show: false
                }
            }
        }));
    };

    onDoubleClickEvent = (event) => {
        this.openEventModal({
            ...event,
            uuid: event?.id ?? event?.uuid ?? null,
            sourceUuid: event?.sourceUuid ?? null,
            type: event?.type ?? "Event",
            time: {
                start: event?.start ?? null,
                end: event?.end ?? null,
                allDay: !!event?.allDay
            }
        });
    };

    onModalClose = () => {
        this.closeEventModal();
    };

    onModalRemind = async (event) => {
        if (!isGuid(event?.uuid)) {
            return;
        }

        const data = await this.props.mappedEvent({
            uuid: event.uuid
        });

        for (let i = 0; i < (data?.attendees?.length ?? 0); i++) {
            const attendee = data.attendees[i];

            await this.props.sendEvent({
                data,
                email: attendee.email
            });
        }
    };

    onModalDelete = async (event) => {
        if (!isGuid(event?.uuid)) {
            return;
        }

        await this.props.deleteEvent({
            uuid: event.uuid
        });

        this.closeEventModal();

        await this.loadData();
    };

    onModalOk = async (event) => {
        this.closeEventModal();

        const {
            status,
            style,
            title,
            description,
            location,
            type = "Event",
            startsAt: _startsAt,
            endsAt: _endsAt,
            allDay: _allDay,
            rrule,
            uuid,
            sourceUuid,
            calendarUuid,
            createdBy: createdByUuid,
            person: personUuid,
            matter: caseUuid,
            invites
        } = event;

        let startsAt = _startsAt ?? event?.time?.start ?? null;
        let endsAt = _endsAt ?? event?.time?.end ?? null;
        const allDay = _allDay ?? event?.time?.allDay ?? false;

        const recurrence = rrule ?? event?.rrule ?? null;

        const fallbackCalendarUuid =
            this.state.calendars
                ?.find((calendar) => isGuid(calendar?.uuid))
                ?.uuid ?? null;

        const fields = {
            calendarUuid: isGuid(calendarUuid) ? calendarUuid : fallbackCalendarUuid,
            createdByUuid: isGuid(createdByUuid)
                ? createdByUuid
                : this.props.auth.user.uuid,
            status,
            style,
            title,
            type,
            description: normalizeCalendarDescription(description),
            location,
            startsAt,
            endsAt,
            allDay,
            recurrence
        };

        if (!isGuid(fields.calendarUuid)) {
            console.warn("No calendarUuid available; cannot save event.");
            return;
        }

        if (isGuid(uuid)) {
            fields.uuid = uuid;
        }

        if (isGuid(sourceUuid)) {
            fields.sourceUuid = sourceUuid;
        }

        if (isGuid(personUuid)) {
            fields.personUuid = personUuid;
        }

        if (isGuid(caseUuid)) {
            fields.caseUuid = caseUuid;
        }

        if (startsAt && (!endsAt || +new Date(endsAt) === +new Date(startsAt))) {
            endsAt = new Date(+new Date(startsAt) + 30 * 60 * 1000);
            fields.endsAt = endsAt;
        }

        if (Array.isArray(invites)) {
            fields.invites = invites;
        }

        const saved = await this.props.saveEvent(fields);
        const savedUuid = saved?.uuid ?? saved?.data?.uuid ?? null;

        if (type !== "Block" && isGuid(savedUuid)) {
            await this.onModalRemind({
                uuid: savedUuid
            });
        }

        await this.loadData();
    };

    formatTime = (date) => {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            return "";
        }

        return date.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit"
        });
    };

    formatDateHeading = (date) => {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            return "";
        }

        return date.toLocaleDateString([], {
            weekday: "long",
            month: "long",
            day: "numeric"
        });
    };

    formatShortDate = (date) => {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            return "";
        }

        return date.toLocaleDateString([], {
            month: "short",
            day: "numeric"
        });
    };

    getPersonName = (event) => {
        const person = event.person || {};

        return (
            person.displayName ||
            person.name ||
            [person.firstName, person.lastName].filter(Boolean).join(" ") ||
            event.personName ||
            "Person"
        );
    };

    onPersonClick = (event, personUuid) => {
        event.preventDefault();
        event.stopPropagation();

        if (!isGuid(personUuid)) {
            return;
        }

        window.location.href = `/dashboard/people/${personUuid}`;
    };

    groupedEvents = (events) => {
        const groups = [];

        for (let i = 0; i <= 7; i++) {
            const day = new Date(this.state.visible.start);
            day.setDate(day.getDate() + i);

            const key = day.toISOString().slice(0, 10);

            groups.push({
                key,
                date: day,
                events: []
            });
        }

        const byKey = new Map(
            groups.map((group) => [group.key, group])
        );

        events.forEach((event) => {
            const key = event.start.toISOString().slice(0, 10);
            const group = byKey.get(key);

            if (group) {
                group.events.push(event);
            }
        });

        groups.forEach((group) => {
            group.events.sort((a, b) => a.start - b.start);
        });

        return groups;
    };

    getVisibleCalendarUuids = () => {
        const all = (this.state.calendars || [])
            .map((calendar) => calendar.uuid)
            .filter(isGuid);

        if (
            this.state.visibleCalendarUuids === null ||
            typeof this.state.visibleCalendarUuids === "undefined"
        ) {
            return all;
        }

        return Array.isArray(this.state.visibleCalendarUuids)
            ? this.state.visibleCalendarUuids
            : [];
    };

    isEventVisible = (event) => {
        const visibleUuids = this.getVisibleCalendarUuids();
        const visibleSet = new Set(visibleUuids);
        const calendarUuid = event.calendarUuid ?? event.calendar?.uuid ?? null;

        if (!visibleSet.size) {
            return false;
        }

        const calendarVisible = isGuid(calendarUuid)
            ? visibleSet.has(calendarUuid)
            : true;

        if (!calendarVisible) {
            return false;
        }

        return boardTaskEventMatchesMode(
            event,
            this.state.boardTaskMode,
            currentPersonUuid(this.props.auth)
        );
    };

    renderCalendarFilter = () => {
        const all = (this.state.calendars || [])
            .map((calendar) => calendar.uuid)
            .filter(isGuid);

        const effectiveVisible = this.getVisibleCalendarUuids();
        const boardTaskUuids = this.boardTaskCalendarUuids();

        if (all.length === 0) {
            return null;
        }

        return (
            <div className="dashboard-agenda-calendar-bar">
                {boardTaskUuids.length > 0 ? (
                    <div className="dashboard-agenda-filter-group">
                        <span
                            className="dashboard-agenda-color-dot"
                            style={{ background: this.calendarColor("Board Tasks") }}
                        />

                        <span>Board Tasks</span>

                        <SegmentedControl
                            ariaLabel="Board task calendar mode"
                            value={this.state.boardTaskMode}
                            onChange={this.setBoardTaskMode}
                            options={BOARD_TASK_MODE_OPTIONS}
                        />
                    </div>
                ) : null}

                {this.calendarFilterGroups()
                    .map((group) => {
                        const checked = group.uuids.every((uuid) => effectiveVisible.includes(uuid));
                        const partiallyChecked = !checked && group.uuids.some((uuid) => effectiveVisible.includes(uuid));
                        const color = this.calendarDisplayColor(group.calendar);

                        return (
                            <label
                                key={group.key}
                                className="dashboard-agenda-calendar-option"
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    ref={(input) => {
                                        if (input) input.indeterminate = partiallyChecked;
                                    }}
                                    onChange={() => this.toggleCalendarGroupVisible(group.uuids)}
                                />

                                <span
                                    className="dashboard-agenda-color-dot"
                                    style={{ background: color }}
                                />

                                <span>
                                    {group.label}
                                </span>
                            </label>
                        );
                    })}

                <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={this.showAllCalendars}
                >
                    All
                </Button>

                <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={this.showNoCalendars}
                >
                    None
                </Button>
            </div>
        );
    };

    renderEventRow = (event) => {
        const personUuid = event.personUuid;
        const personName = this.getPersonName(event);
        const color = this.calendarDisplayColor(event.calendar, event.calendarUuid);

        return (
            <div
                key={event.id}
                className="py-2 border-bottom"
                onDoubleClick={() => this.onDoubleClickEvent(event)}
                style={{
                    cursor: "pointer"
                }}
            >
                <Row className="align-items-center g-2">
                    <Col xs="3" md="2">
                        <div className="small text-muted">
                            {event.allDay ? "All day" : this.formatTime(event.start)}
                        </div>
                    </Col>

                    <Col xs="4" md="3" className="text-truncate">
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                maxWidth: "100%"
                            }}
                        >
                            <span
                                style={{
                                    width: "9px",
                                    height: "9px",
                                    background: color,
                                    borderRadius: "50%",
                                    display: "inline-block",
                                    marginRight: "6px",
                                    flex: "0 0 auto"
                                }}
                            />

                            <span
                                className="text-truncate"
                                title={personName}
                            >
                                {personName}
                            </span>

                            {isGuid(personUuid) ? (
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 ms-1"
                                    title="Open person"
                                    onClick={(clickEvent) => this.onPersonClick(clickEvent, personUuid)}
                                    style={{
                                        flex: "0 0 auto"
                                    }}
                                >
                                    <CsLineIcons size="12" icon="arrow-bottom-left" />
                                </button>
                            ) : null}
                        </span>
                    </Col>

                    <Col xs="5" md="7" className="text-truncate">
                        <div
                            style={{ fontWeight: 500 }}
                            title={event.title}
                        >
                            {event.title}
                        </div>
                    </Col>
                </Row>
            </div>
        );
    };

    renderAgenda = () => {
        const filteredEvents = (this.state.events || []).filter(this.isEventVisible);

        if (filteredEvents.length === 0) {
            return (
                <div className="text-muted py-3">
                    No upcoming events for the selected calendars.
                </div>
            );
        }

        const groups = this.groupedEvents(filteredEvents)
            .filter((group) => group.events.length > 0);

        return (
            <div>
                {groups.map((group) => (
                    <div key={group.key} className="mb-3">
                        <div className="fw-bold mb-1">
                            {this.formatDateHeading(group.date)}
                        </div>

                        {group.events.map(this.renderEventRow)}
                    </div>
                ))}
            </div>
        );
    };

    render() {
        return (
            <Card style={{ height: "100%" }}>
                <Card.Header>
                    <Row className="align-items-center">
                        <Col>
                            <strong>Agenda</strong>
                        </Col>

                        <Col md="5" className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={this.onTodayClick}
                                >
                                    Today
                                </Button>

                                <Form.Group className="mb-0">
                                    <DatePicker
                                        className="form-control"
                                        selected={this.state.selectedDate}
                                        onChange={this.onDateChange}
                                        dateFormat="MMMM d, yyyy"
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                    </Row>
                </Card.Header>

                <Card.Body>
                    {this.state.error ? (
                        <Alert variant="danger">
                            {this.state.error}
                        </Alert>
                    ) : null}

                    {this.renderCalendarFilter()}

                    <div className="small text-muted mb-2">
                        Showing {this.formatShortDate(this.state.visible.start)} through{" "}
                        {this.formatShortDate(this.state.visible.end)}
                    </div>

                    {this.state.loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        this.renderAgenda()
                    )}
                </Card.Body>

                <EventModal
                    {...this.state.modal.event}
                    calendars={this.state.calendars}
                    onOk={this.onModalOk}
                    onCancel={this.onModalClose}
                    onDelete={this.onModalDelete}
                    onRemind={this.onModalRemind}
                    size="xl"
                />
            </Card>
        );
    }
}

Agenda.propTypes = {
    auth: PropTypes.object.isRequired,
    myCalendars: PropTypes.func.isRequired,
    listEvents: PropTypes.func.isRequired,
    saveEvent: PropTypes.func.isRequired,
    deleteEvent: PropTypes.func.isRequired,
    sendEvent: PropTypes.func.isRequired,
    mappedEvent: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps, {
    myCalendars,
    listEvents,
    saveEvent,
    deleteEvent,
    sendEvent,
    mappedEvent
}, null, {
    forwardRef: true
})(Agenda);
